import styled from "@emotion/styled";

import {
  MDSActionMenuDropdown,
  MDSButton,
  MDSButtonSize,
  MDSButtonVariant,
  MDSCard,
  MDSCardVariant,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
  MDSIconKind,
  MDSMenuContentItemKind,
  MDSMenuDropdownSlotName,
  MDSSeparator,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { AgentLinkButton } from "@/pages/agent/components/agent-link-button";
import { AgentOpenTasksBadge } from "@/pages/agent/components/agent-open-tasks-badge";
import { AgentTaskStateIcon } from "@/pages/agent/components/agent-task-state-icon";
import {
  ProjectSectionKind,
  type ProjectCardViewModel,
} from "@/pages/agent/projects/agent-projects-page.utils";
import { formatAgentDate } from "@/pages/agent/utils/agent-date.utils";
import { decodeAgentDisplayText } from "@/pages/agent/utils/agent-display-text.utils";
import { AssistantTaskStatus } from "@/store/assistant/types";

export const AgentProjectCard = ({
  kind,
  projectCard,
  onOpenActivities,
  onOpenProject,
  onOpenProjectTasks,
  onOpenTask,
}: {
  kind: ProjectSectionKind;
  projectCard: ProjectCardViewModel;
  onOpenActivities: ({ projectId }: { projectId: string }) => void;
  onOpenProject: ({ projectId }: { projectId: string }) => void;
  onOpenProjectTasks: ({ projectId }: { projectId: string }) => void;
  onOpenTask: ({ taskId }: { taskId: string }) => void;
}) => {
  const projectTitle = decodeAgentDisplayText({
    value: projectCard.project.title || "Untitled project",
  });
  const activityMenu = [
    {
      id: "view-activity-log",
      kind: MDSMenuContentItemKind.Item,
      label: "View activity log",
      icon: MDSIconKind.History,
      onClick: () => onOpenActivities({ projectId: projectCard.project.id }),
    },
  ];
  const openProject = () => {
    onOpenProject({ projectId: projectCard.project.id });
  };

  return (
    <StyledProjectCardInteraction
      aria-label={`Open ${projectTitle}`}
      role="group"
      tabIndex={0}
      onClick={openProject}
      onKeyDown={event => {
        if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          openProject();
        }
      }}
    >
      <StyledProjectCard variant={MDSCardVariant.Chrome}>
        <StyledCardHeader>
          <StyledTitleRow>
            {kind === ProjectSectionKind.Completed && (
              <AgentTaskStateIcon status={AssistantTaskStatus.Completed} size={14} />
            )}
            <StyledProjectTitleBoundary
              onClick={event => {
                event.stopPropagation();
              }}
            >
              <StyledProjectTitleButton
                truncate
                variant={MDSTextVariant.TextM}
                onClick={openProject}
              >
                {projectTitle}
              </StyledProjectTitleButton>
            </StyledProjectTitleBoundary>
            {projectCard.openTasksCount > 0 ? (
              <AgentOpenTasksBadge openTasksCount={projectCard.openTasksCount} />
            ) : null}
          </StyledTitleRow>

          <StyledCardActions
            onClick={event => {
              event.stopPropagation();
            }}
          >
            <MDSActionMenuDropdown contentList={activityMenu} placement="bottom right">
              <MDSIconButton
                aria-label={`More actions for ${projectTitle}`}
                iconKind={MDSIconKind.More}
                size={MDSIconButtonSize.XSmall}
                slot={MDSMenuDropdownSlotName.Trigger}
                variant={MDSIconButtonVariant.Transparent}
                rounded
              />
            </MDSActionMenuDropdown>
          </StyledCardActions>
        </StyledCardHeader>

        <StyledCardSeparator orientation="horizontal" />

        <StyledDescription
          color={MDSTextColor.Secondary}
          numberOfLines={3}
          variant={MDSTextVariant.ParagraphS}
        >
          {decodeAgentDisplayText({
            value: projectCard.project.description || "No description yet.",
          })}
        </StyledDescription>

        <StyledTasksSection>
          <StyledTasksHeader>
            <MDSText
              color={MDSTextColor.Primary}
              variant={MDSTextVariant.TextS}
              weight={MDSTextWeight.SemiBold}
            >
              Open tasks
            </MDSText>
            {projectCard.taskCount > 0 ? (
              <StyledSeeAllBoundary onClick={event => event.stopPropagation()}>
                <MDSButton
                  size={MDSButtonSize.Small}
                  variant={MDSButtonVariant.Transparent}
                  onPress={() => onOpenProjectTasks({ projectId: projectCard.project.id })}
                >
                  See all
                </MDSButton>
              </StyledSeeAllBoundary>
            ) : null}
          </StyledTasksHeader>
          {projectCard.previewTasks.length > 0 ? (
            <StyledTaskPreviewList>
              {projectCard.previewTasks.map(task => (
                <StyledTaskPreviewRow key={task.id}>
                  <AgentTaskStateIcon status={task.status} size={14} />
                  <StyledTaskLinkBoundary onClick={event => event.stopPropagation()}>
                    <StyledTaskLink truncate onClick={() => onOpenTask({ taskId: task.id })}>
                      {decodeAgentDisplayText({ value: task.title || "Untitled task" })}
                    </StyledTaskLink>
                  </StyledTaskLinkBoundary>
                  {task.dueAt ? (
                    <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextXs}>
                      {formatAgentDate({ value: task.dueAt })}
                    </MDSText>
                  ) : null}
                </StyledTaskPreviewRow>
              ))}
            </StyledTaskPreviewList>
          ) : (
            <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextS}>
              No open tasks
            </MDSText>
          )}
        </StyledTasksSection>
      </StyledProjectCard>
    </StyledProjectCardInteraction>
  );
};

const StyledProjectCard = styled(MDSCard)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  min-height: 248px;
  min-width: 0;
  height: 100%;

  &:hover,
  &:focus-within {
    background: ${({ theme }) => theme.colors.background.list.hover};
  }
`;

const StyledProjectCardInteraction = styled.div`
  cursor: pointer;
  min-width: 0;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.border.emphasis};
    outline-offset: 2px;
    border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  }
`;

const StyledCardHeader = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: space-between;
  min-width: 0;
`;

const StyledTitleRow = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  min-width: 0;
`;

const StyledProjectTitleButton = styled(AgentLinkButton)`
  flex: 0 1 auto;
  min-width: 0;

  && {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: ${({ theme }) => theme.fontWeights.smedium};
    text-decoration: none;
  }

  &&:hover:not([data-disabled]),
  &&[data-focus-visible] {
    color: ${({ theme }) => theme.colors.text.primary};
    text-decoration: none;
  }
`;

const StyledProjectTitleBoundary = styled.div`
  flex: 0 1 auto;
  min-width: 0;
`;

const StyledCardActions = styled.div`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const StyledCardSeparator = styled(MDSSeparator)`
  margin: 0;
`;

const StyledDescription = styled(MDSText)`
  overflow-wrap: anywhere;
`;

const StyledTasksSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  min-width: 0;
`;

const StyledTasksHeader = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const StyledSeeAllBoundary = styled.div`
  margin: ${({ theme }) => `-${theme.spacing[2]} -${theme.spacing[2]} -${theme.spacing[2]} 0`};
`;

const StyledTaskPreviewList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const StyledTaskPreviewRow = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  min-width: 0;
`;

const StyledTaskLink = styled(AgentLinkButton)`
  min-width: 0;

  && {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: ${({ theme }) => theme.fontSizes[MDSTextVariant.TextS]};
    text-decoration: none;
  }

  &&:hover:not([data-disabled]),
  &&[data-focus-visible] {
    color: ${({ theme }) => theme.colors.text.primary};
    text-decoration-line: underline;
    text-decoration-style: dotted;
  }
`;

const StyledTaskLinkBoundary = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;
